import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Stethoscope, PlusCircle, Flag, Calendar, ShieldCheck, Clock, Activity, Wallet, BellRing, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, Badge, Avatar, EmptyState, statusTone } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import ProDocuments from "@/pages/ProDocuments";
import VisitReportForm from "@/pages/pro/VisitReportForm";
import ChangeRequestModal from "@/pages/pro/ChangeRequestModal";
import BadgeRiskBanner from "@/pages/pro/BadgeRiskBanner";
import { sessionTag } from "@/components/ShiftCheck";
import { CycleBadge, CycleHistory, sessionsLabel, nextSessionNo } from "@/components/Cycle";
import RosterViews, { ymd } from "@/components/RosterViews";
import api, { API } from "@/lib/api";

const REQUIRED_DOCS = ["photo", "registration", "membership", "insurance"];

export default function Pro() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [sched, setSched] = useState({});
  const [pain, setPain] = useState({});
  const [earn, setEarn] = useState(null);
  const [roster, setRoster] = useState([]);
  const [docs, setDocs] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [reportFor, setReportFor] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const load = useCallback(() => { api.get("/pro/cases").then((r) => setCases(r.data)); api.get("/pro/earnings").then((r) => setEarn(r.data)); api.get("/pro/roster").then((r) => setRoster(r.data)).catch(() => setRoster([])); }, []);
  const loadDocs = useCallback(() => {
    if (!user?.id) return Promise.resolve();
    return api.get(`/users/${user.id}/documents`).then((r) => {
      setDocs(r.data);
      const present = new Set(r.data.map((d) => d.doc_type));
      if (present.has("insurance_waiver")) present.add("insurance");
      const missing = REQUIRED_DOCS.filter((k) => !present.has(k));
      if (missing.length === REQUIRED_DOCS.length && user?.credential_status !== "Verified") setShowDocs(true);
    });
  }, [user?.id, user?.credential_status]);
  useEffect(() => { load(); loadDocs(); }, [load, loadDocs]);

  const presentTypes = new Set((docs || []).map((d) => d.doc_type));
  if (presentTypes.has("insurance_waiver")) presentTypes.add("insurance");
  const missingRequired = docs ? REQUIRED_DOCS.filter((k) => !presentTypes.has(k)) : [];
  const verified = user?.credential_status === "Verified";

  const logSession = (c) => { setPrefill(null); setReportFor(c); };
  const startVisit = (c, item) => {
    document.querySelector(`[data-testid="pro-case-${c.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPrefill({ date: item.date, start: (item.start || "").slice(0, 5), end: (item.end || "").slice(0, 5) });
    setReportFor(c);
  };
  const submitSession = async (f) => {
    const c = reportFor;
    const oc = c.outcome || {};
    const next = nextSessionNo(oc);
    const p = pain[c.id] || {};
    const payload = { sessions_completed: next, adherence: 90, report: f };
    if (p.score != null) payload.pain_score = Number(p.score);
    if (p.note) payload.note = p.note;
    await api.post(`/referrals/${c.id}/sessions`, payload);
    toast.success(`Session logged (${next}${oc.sessions_total > 0 ? `/${oc.sessions_total}` : ""}) — visit report saved to Ops`);
    setPain({ ...pain, [c.id]: { score: null, note: "" } });
    setReportFor(null);
    load();
  };
  const markMilestone = async (c) => {
    const pm = c.outcome?.plan_milestones || [];
    const idx = pm.findIndex((m) => !m.done);
    if (idx < 0) return toast("All milestones already marked");
    await api.post(`/referrals/${c.id}/sessions`, { milestone_index: idx });
    toast.success(`Milestone marked: ${pm[idx].label}`);
    load();
  };
  const addSchedule = async (c) => {
    const v = sched[c.id];
    if (!v?.date) return toast.error("Pick a date");
    await api.post(`/referrals/${c.id}/schedule`, { date: v.date, time: v.time || "" });
    toast.success("Session scheduled — patient notified");
    setSched({ ...sched, [c.id]: { date: "", time: "" } });
    load();
  };

  return (
    <>
      {showDocs ? (
        <ProDocuments userId={user.id} credentialStatus={user?.credential_status} onDone={async () => { await loadDocs(); setShowDocs(false); }} />
      ) : (
      <>
      <PageHead title="Clinician Console" subtitle="Physiotherapist console — view your assigned cases, log sessions & pain scores, and mark milestones."
        action={<div className="flex items-center gap-2"><Badge tone="teal"><Stethoscope size={13} /> Physiotherapist app</Badge><Link to="/pro/settings"><Btn size="sm" data-testid="pro-settings-link"><Settings size={13} /> Settings</Btn></Link></div>} />

      <BadgeRiskBanner onUpload={() => setShowDocs(true)} />

      <button onClick={() => setShowDocs(true)} data-testid="pro-credentials-banner"
        className={`w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-[12.5px] font-medium mb-4 transition-colors ${verified ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}>
        {verified ? <ShieldCheck size={15} /> : <Clock size={15} />}
        <span className="text-left flex-1">
          {verified ? "Credentials verified" : missingRequired.length > 0 ? `${missingRequired.length} required document${missingRequired.length > 1 ? "s" : ""} still needed` : "Documents submitted — pending verification"}
        </span>
        <span className="font-semibold underline">Manage →</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 items-stretch [&>*]:min-w-0 [&>*]:mb-0" data-testid="pro-top-row">
        <div className="lg:col-span-2 [&>*]:mb-0 [&>*]:h-full"><TodayStrip roster={roster} cases={cases} onOpen={startVisit} /></div>
        {earn && (
          <Card className="p-5 bg-teal-50 border-teal-100 h-full flex flex-col" data-testid="pro-earnings">
            <div className="text-[12px] font-semibold text-teal-800 flex items-center gap-1.5"><Wallet size={14} /> My earnings</div>
            <div className="metric text-3xl font-extrabold text-teal-800 leading-none mt-3">{earn.currency}{Number(earn.expected).toLocaleString("en-IN")}</div>
            <div className="text-[11.5px] text-teal-700 mt-1">expected across {earn.cases} case{earn.cases === 1 ? "" : "s"}</div>
            <div className="grid grid-cols-2 gap-3 mt-auto pt-4">
              <div className="rounded-lg bg-white/70 p-3"><div className="metric font-extrabold text-[16px] text-slate-900">{earn.currency}{Number(earn.disbursed).toLocaleString("en-IN")}</div><div className="text-[11px] text-slate-500">Disbursed</div></div>
              <div className="rounded-lg bg-white/70 p-3"><div className="metric font-extrabold text-[16px] text-slate-900">{earn.currency}{Number(earn.pending).toLocaleString("en-IN")}</div><div className="text-[11px] text-slate-500">Pending</div></div>
            </div>
          </Card>
        )}
      </div>

      <UpcomingSessionAlerts roster={roster} cases={cases} />

      {reportFor && <VisitReportForm c={reportFor} prefill={prefill} onClose={() => setReportFor(null)} onSubmit={submitSession} />}

      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-display font-semibold text-[15px] text-slate-900">My cases</h3>
        <Badge tone="slate">{cases.length}</Badge>
      </div>

      {cases.length === 0 ? (
        <Card><EmptyState icon={<Stethoscope size={24} />} title="No assigned cases yet"
          subtitle="When a doctor submits a treatment order, the matching engine assigns it to a physiotherapist and it appears here." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(520px,1fr))] gap-4 items-start mb-4 [&>*]:min-w-0" data-testid="pro-cases-grid">
          {cases.map((c) => {
            const oc = c.outcome || {};
            const pmDone = (oc.plan_milestones || []).filter((m) => m.done).length;
            const pmTotal = (oc.plan_milestones || []).length;
            const trend = oc.pain_trend || [];
            const p = pain[c.id] || {};
            return (
              <Card key={c.id} className="p-5" data-testid={`pro-case-${c.id}`}>
                <div className="flex items-start gap-3">
                  <Avatar name={c.patient_name} size={42} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[14px] break-words">{c.patient_name} <span className="text-slate-400 font-normal text-[12px] whitespace-nowrap">· {c.ref_no}</span></div>
                    <div className="text-[12.5px] text-slate-500 break-words">{c.diagnosis_label}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-2.5">
                  <Badge tone={statusTone(c.status)}>{oc.status || c.status}</Badge>
                  {c.accepted ? <Badge tone="emerald">Assigned</Badge> : <Badge tone="teal">Assigned by Ops</Badge>}
                  <CycleBadge cycle={c.cycle} testid={`pro-cycle-${c.id}`} />
                </div>

                <div className="flex items-center justify-between gap-3 text-[12px] text-slate-500 mt-4 mb-1">
                  <span data-testid={`pro-sessions-${c.id}`}>{sessionsLabel(oc)} · milestones {pmDone}/{pmTotal}</span>
                  <span>{oc.progress || 0}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-600 transition-all" style={{ width: `${oc.progress || 0}%` }} /></div>
                <div className="mt-2"><CycleHistory h={c.cycle_history} testid={`pro-cycles-${c.id}`} /></div>

                {trend.length > 0 && (
                  <div className="mt-3">
                    <div className="pq-label mb-1 flex items-center gap-1.5"><Activity size={13} className="text-teal-600" /> Pain trend (latest {Math.min(trend.length, 12)})</div>
                    <PainSparkline data={trend} />
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-dashed border-slate-200">
                  <div className="pq-label mb-1.5">Log a session — home visit report required</div>
                  <div className="grid grid-cols-6 sm:grid-cols-11 gap-1 mb-2">
                    <span className="col-span-6 sm:col-span-11 text-[11.5px] text-slate-500">Pain today:</span>
                    {[0,1,2,3,4,5,6,7,8,9,10].map((n) => (
                      <button key={n} data-testid={`pro-pain-${c.id}-${n}`} onClick={() => setPain({ ...pain, [c.id]: { ...p, score: n } })}
                        className={`h-8 rounded-md text-[12px] font-semibold transition-colors ${p.score === n ? "bg-[#1A3A6D] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{n}</button>
                    ))}
                  </div>
                  <input value={p.note || ""} onChange={(e) => setPain({ ...pain, [c.id]: { ...p, note: e.target.value } })} placeholder="Session note (optional)" className="pq-input py-1.5 mb-2 w-full" data-testid={`pro-note-${c.id}`} />
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Btn size="sm" onClick={() => logSession(c)} disabled={c.status === "Completed"} data-testid={`pro-log-${c.id}`}><PlusCircle size={14} /> Log Session</Btn>
                  <Btn size="sm" onClick={() => markMilestone(c)} disabled={pmDone >= pmTotal} data-testid={`pro-milestone-${c.id}`}><Flag size={14} /> Mark Milestone</Btn>
                </div>

                <div className="mt-4 pt-3 border-t border-dashed border-slate-200">
                  <div className="pq-label mb-2 flex items-center gap-1.5"><Calendar size={13} /> Schedule a session</div>
                  <div className="flex gap-2 flex-wrap items-center">
                    <input type="date" className="pq-input py-1.5 flex-1 min-w-[150px]" value={sched[c.id]?.date || ""} onChange={(e) => setSched({ ...sched, [c.id]: { ...sched[c.id], date: e.target.value } })} data-testid={`sched-date-${c.id}`} />
                    <input type="time" className="pq-input py-1.5 flex-1 min-w-[120px]" value={sched[c.id]?.time || ""} onChange={(e) => setSched({ ...sched, [c.id]: { ...sched[c.id], time: e.target.value } })} data-testid={`sched-time-${c.id}`} />
                    <Btn size="sm" variant="primary" onClick={() => addSchedule(c)} data-testid={`sched-add-${c.id}`}>Schedule</Btn>
                  </div>
                  {(c.schedule || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {c.schedule.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 flex-wrap text-[12.5px] bg-slate-50 rounded-lg px-3 py-1.5">
                          <Calendar size={12} className="text-teal-600" />
                          <span className="font-semibold">{s.date}{s.time ? ` · ${s.time}` : ""}</span>
                          <Badge tone={s.status === "Confirmed" ? "emerald" : "amber"} className="ml-auto">{s.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  <CaseRosterShifts roster={roster} patientId={c.patient_id} patientName={c.patient_name} />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <MyRoster roster={roster} onReload={load} />
      </>
      )}
    </>
  );
}

// Vendor billing is blocked in the clinician portal for now (kept for re-enable).
// eslint-disable-next-line no-unused-vars
function VendorBilling({ currency }) {
  const [invoices, setInvoices] = useState(null);
  const [billable, setBillable] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [period, setPeriod] = useState(new Date().toLocaleString("en-US", { month: "short", year: "numeric" }));
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get("/pro/invoices").then((r) => setInvoices(r.data));
    api.get("/pro/billable").then((r) => { setBillable(r.data); setAmount(String(r.data.suggested_amount || "")); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const cur = billable?.currency || currency;
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount greater than zero");
    setSaving(true);
    try {
      await api.post("/pro/invoices", { period, amount: amt, note });
      toast.success("Invoice submitted — pending PranIQ Operations verification");
      setShowForm(false); setNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit invoice"); }
    setSaving(false);
  };
  const tone = (s) => (s === "Paid" ? "emerald" : s === "Approved" ? "sky" : s === "Rejected" ? "rose" : "amber");

  return (
    <Card className="p-5 mb-4" data-testid="pro-billing">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-semibold text-[14px] flex items-center gap-2"><Wallet size={16} className="text-teal-600" /> Vendor Billing</div>
        <Btn size="sm" variant="primary" onClick={() => setShowForm((v) => !v)} data-testid="pro-raise-invoice">{showForm ? "Close" : "Raise invoice"}</Btn>
      </div>
      <p className="text-[12px] text-slate-500 mb-3">Submit a payout invoice for your completed cases. PranIQ Operations verifies &amp; approves it.</p>

      {showForm && (
        <div className="rounded-xl bg-slate-50 p-4 mb-4 space-y-3" data-testid="pro-invoice-form">
          {billable && <div className="text-[12px] text-slate-500">Suggested from {billable.line_items.length} completed case{billable.line_items.length !== 1 ? "s" : ""}: <b className="text-slate-700">{cur}{Number(billable.suggested_amount).toLocaleString("en-IN")}</b></div>}
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="pq-label">Period</span><input className="pq-input py-1.5" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="pro-invoice-period" /></label>
            <label className="block"><span className="pq-label">Amount ({cur})</span><input type="number" className="pq-input py-1.5" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pro-invoice-amount" /></label>
          </div>
          <label className="block"><span className="pq-label">Note (optional)</span><input className="pq-input py-1.5" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pro-invoice-note" /></label>
          <Btn size="sm" variant="primary" onClick={submit} disabled={saving} data-testid="pro-invoice-submit">{saving ? "Submitting…" : "Submit for verification"}</Btn>
        </div>
      )}

      {invoices === null ? null : invoices.length === 0 ? (
        <p className="text-[12.5px] text-slate-400">No invoices submitted yet.</p>
      ) : (
        <div className="space-y-2">
          {invoices.map((i) => (
            <div key={i.id} className="flex items-center gap-3 rounded-lg bg-white border border-slate-100 px-3 py-2.5" data-testid={`pro-invoice-${i.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold truncate">{i.invoice_no} <span className="text-slate-400 font-normal">· {i.period}</span></div>
                <div className="metric text-[13px] font-bold text-slate-800">{i.currency}{Number(i.amount).toLocaleString("en-IN")}</div>
                {i.status === "Rejected" && i.reject_reason && <div className="text-[11px] text-rose-500 mt-0.5">Rejected: {i.reject_reason}</div>}
              </div>
              <Badge tone={tone(i.status)}>{i.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function todayStr() { return new Date().toISOString().slice(0, 10); }

function UpcomingSessionAlerts({ roster, cases }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  const horizon = new Date(now.getTime() + 60 * 60000);
  const upcoming = [];
  (roster || []).forEach((s) => {
    const dt = new Date(`${s.date}T${String(s.start || "09:00").slice(0, 5)}:00+05:30`);
    if (!isNaN(dt) && dt > now && dt <= horizon)
      upcoming.push({ key: `shift-${s.id}`, label: s.patient_name || "Roster shift", dt, location: s.location });
  });
  (cases || []).forEach((c) => (c.schedule || []).forEach((s) => {
    if (!s.time || s.status === "Cancelled") return;
    const dt = new Date(`${s.date}T${String(s.time).slice(0, 5)}:00+05:30`);
    if (!isNaN(dt) && dt > now && dt <= horizon)
      upcoming.push({ key: `sess-${s.id}`, label: c.patient_name, dt, location: "" });
  }));
  if (upcoming.length === 0) return null;
  upcoming.sort((a, b) => a.dt - b.dt);
  return (
    <Card className="p-4 mb-4 bg-amber-50 border-amber-200" data-testid="pro-upcoming-alerts">
      <div className="flex items-center gap-2 text-amber-800 font-semibold text-[13px]">
        <BellRing size={15} /> Session{upcoming.length > 1 ? "s" : ""} coming up in the next hour
      </div>
      <div className="mt-2 space-y-1.5">
        {upcoming.map((u) => {
          const mins = Math.max(1, Math.round((u.dt - now) / 60000));
          return (
            <div key={u.key} className="flex items-center gap-2 flex-wrap text-[12.5px] text-amber-900 bg-white/60 rounded-lg px-3 py-1.5" data-testid={`pro-upcoming-${u.key}`}>
              <Clock size={12} />
              <span className="font-semibold">{u.label}</span>
              <span>starts in {mins} min ({u.dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})</span>
              {u.location && <span className="text-amber-700">· {u.location}</span>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TodayStrip({ roster, cases, onOpen }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  const today = ymd(now);
  const nowHM = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const items = [
    ...(roster || []).filter((s) => s.date === today).map((s) => ({ key: `shift-${s.id}`, kind: "Shift", date: s.date, start: s.start || "09:00", end: s.end || "", who: s.patient_name || "General shift", where: s.location, si: s.session_info, c: s.patient_id ? (cases || []).find((c) => c.patient_id === s.patient_id) : null })),
    ...(cases || []).flatMap((c) => (c.schedule || []).filter((s) => s.date === today && s.status !== "Cancelled")
      .map((s) => ({ key: `sess-${s.id}`, kind: "Session", date: s.date, start: s.time || "", end: "", who: c.patient_name, where: c.ref_no, c }))),
  ].sort((a, b) => (a.start || "99").localeCompare(b.start || "99"));
  const nextIdx = items.findIndex((i) => !i.start || (i.end ? i.end > nowHM : i.start >= nowHM));
  const state = (i, idx) => {
    if (i.start && i.start <= nowHM && i.end && i.end > nowHM) return ["Now", "bg-teal-600 text-white"];
    if (idx === nextIdx) return ["Next", "bg-[#1A3A6D] text-white"];
    if (idx < nextIdx || nextIdx === -1) return ["Done", "bg-slate-200 text-slate-600"];
    return ["Later", "bg-slate-100 text-slate-500"];
  };
  return (
    <Card className="p-4 mb-4" data-testid="pro-today-strip">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="font-semibold text-[14px] flex items-center gap-2"><Calendar size={16} className="text-teal-600" /> Today</div>
        <span className="text-[12px] text-slate-500">{now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</span>
        <Badge tone="slate" className="ml-auto">{items.length} item{items.length === 1 ? "" : "s"}</Badge>
      </div>
      {items.length === 0 ? (
        <p className="text-[12.5px] text-slate-400" data-testid="pro-today-empty">Nothing scheduled for today.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" data-testid="pro-today-list">
          {items.map((i, idx) => {
            const [label, cls] = state(i, idx);
            const tappable = i.c && i.c.status !== "Completed";
            const Tag = tappable ? "button" : "div";
            return (
              <Tag key={i.key} type={tappable ? "button" : undefined} onClick={tappable ? () => onOpen(i.c, i) : undefined}
                className={`min-w-0 w-full text-left rounded-xl border p-3 ${tappable ? "cursor-pointer transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" : ""} ${label === "Done" ? "border-slate-100 bg-slate-50/70 opacity-70" : label === "Later" ? "border-slate-200 bg-white" : "border-teal-200 bg-teal-50/50"}`} data-testid={`pro-today-${i.key}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="metric font-extrabold text-[15px] text-slate-900">{i.start || "Any time"}{i.end ? `–${i.end}` : ""}</span>
                  <span className={`ml-auto rounded-full px-2 py-0.5 text-[10.5px] font-bold ${cls}`}>{label}</span>
                </div>
                <div className="text-[13px] font-semibold text-slate-800 break-words">{i.who}</div>
                <div className="text-[11.5px] text-slate-500 break-words">{i.kind} · {i.where || "—"}</div>
                {i.si && <div className="text-[11px] font-semibold text-sky-800 mt-0.5" data-testid={`pro-today-session-${i.key}`}>{sessionTag(i.si)}</div>}
                {tappable && <div className="mt-1.5 text-[11.5px] font-semibold text-teal-700" data-testid={`pro-today-start-${i.key}`}>Start visit report →</div>}
              </Tag>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function MyRoster({ roster, onReload }) {
  const upcoming = (roster || []).filter((s) => s.date >= todayStr()).length;
  const [req, setReq] = useState(null);
  return (
    <Card className="p-5 mb-4" data-testid="pro-roster">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-semibold text-[14px] flex items-center gap-2"><Calendar size={16} className="text-teal-600" /> My Roster</div>
        <Badge tone="slate">{upcoming} upcoming</Badge>
      </div>
      <p className="text-[12px] text-slate-500 mb-3">Shifts scheduled for you by PranIQ Operations. Need a different date? Use "Request change" — Ops makes the change.</p>
      <RosterViews shifts={roster || []} prefix="pro-roster" onRequest={setReq} />
      {req && <ChangeRequestModal shift={req} onClose={() => setReq(null)} onDone={() => { setReq(null); onReload(); }} />}
    </Card>
  );
}

function CaseRosterShifts({ roster, patientId, patientName }) {
  const today = todayStr();
  const shifts = (roster || []).filter((s) => s.date >= today && ((patientId && s.patient_id === patientId) || (patientName && s.patient_name === patientName))).sort((a, b) => a.date.localeCompare(b.date));
  if (shifts.length === 0) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="pro-case-roster">
      <div className="pq-label flex items-center gap-1.5"><Calendar size={12} className="text-teal-600" /> Ops roster</div>
      {shifts.map((s) => (
        <div key={s.id} className="flex items-center gap-2 flex-wrap text-[12.5px] bg-teal-50/60 rounded-lg px-3 py-1.5">
          <Clock size={12} className="text-teal-600" />
          <span className="font-semibold">{s.date} · {s.start}–{s.end}</span>
          <span className="text-slate-500 ml-auto">{s.location}</span>
        </div>
      ))}
    </div>
  );
}

export function PainSparkline({ data }) {
  const pts = data.slice(-12);
  return (
    <div className="flex items-end gap-1 h-12" data-testid="pain-sparkline">
      {pts.map((v, i) => {
        const c = v >= 7 ? "bg-rose-500" : v >= 4 ? "bg-amber-500" : "bg-emerald-500";
        return <div key={i} className="flex-1 max-w-[36px] h-full flex flex-col items-center justify-end" title={`${v}/10`}>
          <div className={`w-full rounded-t ${c}`} style={{ height: `${Math.max(8, (v / 10) * 75)}%` }} />
          <span className="text-[9px] text-slate-400 mt-0.5">{v}</span>
        </div>;
      })}
    </div>
  );
}
